import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { User, Lock, Eye, EyeOff, Loader, AlertTriangle } from 'lucide-react';

const Login = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await login(formData.username, formData.password);
            navigate('/');
        } catch (err) {
            setError(err.message || 'An unknown error occurred.');
            setLoading(false);
        }
    };

    return (
        <>
        <Helmet>
            <title>Sign In | RatingApp — Earn USDT by Rating Apps</title>
            <meta name="description" content="Sign in to your RatingApp account and start earning USDT today by rating top apps." />
        </Helmet>
        <div className="flex items-center justify-center min-h-screen  from-gray-900 via-blue-900/30 to-gray-900 text-white p-4">
            <motion.div 
                className="w-full max-w-md"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="bg-gray-800/30 backdrop-blur-xl border border-white/10 shadow-2xl shadow-cyan-500/10 rounded-3xl p-8">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                            Welcome Back
                        </h1>
                        <p className="text-slate-400 mt-2">Sign in to continue to your account.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Username Input */}
                        <div className="relative">
                             <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                placeholder="Username"
                                required
                                className="w-full bg-gray-900/50 border border-transparent hover:border-white/20 focus:border-cyan-500 rounded-xl py-3 pl-12 pr-4 text-white placeholder-slate-400 transition-all duration-300 focus:ring-2 focus:ring-cyan-500/50"
                            />
                        </div>

                        {/* Password Input */}
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Password"
                                required
                                className="w-full bg-gray-900/50 border border-transparent hover:border-white/20 focus:border-cyan-500 rounded-xl py-3 pl-12 pr-12 text-white placeholder-slate-400 transition-all duration-300 focus:ring-2 focus:ring-cyan-500/50"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 p-3 rounded-lg"
                                >
                                    <AlertTriangle size={18} />
                                    <span>{error}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <motion.button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-3 rounded-xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            whileHover={{ scale: 1.02, boxShadow: '0 20px 30px -10px rgba(0, 255, 255, 0.25)' }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {loading ? <><Loader className="animate-spin" /> Signing In...</> : 'Sign In'}
                        </motion.button>

                        <div className="text-sm text-center text-slate-400 flex justify-between items-center">
                            <Link to="/forgot" className="hover:text-cyan-400 transition-colors">Forgot Password?</Link>
                            <Link to="/signup" className="hover:text-cyan-400 transition-colors">Don't have an account?</Link>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
        </>
    );
};

export default Login;
